import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePostingSurfacePresence } from '@/features/notifications/hooks/usePostingSurfacePresence';
import { Plus } from 'lucide-react';
import { QuotationWorkspaceLayout } from '../components/workspace/QuotationWorkspaceLayout';
import { QuotationListFilters } from '../components/workspace/QuotationListFilters';
import { QuotationMasterList } from '../components/workspace/QuotationMasterList';
import { QuotationDetailPanel } from '../components/workspace/QuotationDetailPanel';
import { QuotationPinnedStrip } from '../components/workspace/QuotationPinnedStrip';
import {
  type WorkspaceStatusFilter,
  useQuotationWorkspaceStore,
} from '../store/quotationWorkspaceStore';
import { createBlankDraft, useQuotationDraftStore } from '../store/quotationDraftStore';
import { quotationService } from '../services/quotationService';
import type { QuotationListItemDto, QuotationStats } from '../types/quotation.types';
import { useAuthStore } from '@features/authentication/store/authStore';
import { useRecordVisit } from '@/features/app-shell/hooks/useRecordVisit';
import { PERMISSIONS } from '@features/shared/constants/permissions';
import {
  onQuotationEvent,
  onQuotationStreamReconnect,
  useQuotationEventStream,
} from '../hooks/useQuotationEventStream';

const VALID_FILTERS: WorkspaceStatusFilter[] = [
  'active',
  'all',
  'drafts',
  'autodrafts',
  'submitted',
  'sent',
];

// CSC-288 master-detail Quotation workspace.
//
// URL is the source of truth for `selectedId` and `statusFilter`. The store
// holds session-persisted UI state (pinned ids, master ratio, search input).
// On mount we hydrate the URL into the store; on store change we push back to
// the URL with `replace: true` so the back button doesn't fill with every row
// click. Search isn't URL-driven — typing should be cheap, no history entries.
//
// The detail panel (QuotationDetailPanel) is the full inline compose editor and
// the sole quotation edit/post surface (86d3k9589); the legacy standalone
// /quotations/compose page was retired and now redirects here.

export function QuotationWorkspacePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const canCreate = useAuthStore((s) => s.hasPermission(PERMISSIONS.QUOTATIONS_CREATE_QUOTATION));

  const selectedId = useQuotationWorkspaceStore((s) => s.selectedId);
  // N8 — while a cutting list is selected and this tab is focused, its own posting outcome does not
  // also toast: the card in the panel has already said it.
  usePostingSurfacePresence('posting.cl', selectedId ?? null);
  const setSelectedId = useQuotationWorkspaceStore((s) => s.setSelectedId);
  const statusFilter = useQuotationWorkspaceStore((s) => s.statusFilter);
  const setStatusFilter = useQuotationWorkspaceStore((s) => s.setStatusFilter);
  const quickFilter = useQuotationWorkspaceStore((s) => s.quickFilter);
  const setQuickFilter = useQuotationWorkspaceStore((s) => s.setQuickFilter);

  // Used to prefetch the AutoDraftReviewCard's source-email summary without
  // a second round-trip when the user clicks a row that's already on screen.
  const [selectedListItem, setSelectedListItem] = useState<QuotationListItemDto | null>(null);

  /* M4 (86d44z223) — Recent.
     ⚠️ The label is passed ONLY when the loaded list item is the one currently selected. Sales has
     no per-record ROUTE — selection lives in `?id=` — so `selectedListItem` briefly still holds the
     PREVIOUS quotation while the new one loads. Pairing an old label with a new id is exactly the
     wrong-record capture this feature has to avoid, and it is invisible once written. */
  useRecordVisit(
    'quotation',
    selectedId,
    selectedListItem?.id === selectedId ? selectedListItem?.quotationNumber : null
  );

  // CSC-293: stats power the count badges in the new filter dropdown. Refetched
  // whenever the master list refreshes (a save / delete just changed the queue
  // distribution); status / search changes don't invalidate stats since they're
  // global counts. Failures are silent — the dropdown simply shows labels
  // without counts.
  const [stats, setStats] = useState<QuotationStats | null>(null);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const lastUrlSyncRef = useRef<{ id: string | null; filter: string | null }>({
    id: null,
    filter: null,
  });

  // URL -> store hydration. Runs on mount and when the user pastes a deep-
  // linked URL. Subsequent in-app navigation flows the other direction (store
  // -> URL), guarded below to avoid loops.
  useEffect(() => {
    const idParam = searchParams.get('id');
    const filterParam = searchParams.get('filter');

    if (lastUrlSyncRef.current.id !== idParam || lastUrlSyncRef.current.filter !== filterParam) {
      const numericId = idParam ? Number(idParam) : null;
      if (numericId && Number.isFinite(numericId) && numericId > 0) {
        setSelectedId(numericId);
      } else if (idParam === null) {
        setSelectedId(null);
      }

      if (filterParam && VALID_FILTERS.includes(filterParam as WorkspaceStatusFilter)) {
        setStatusFilter(filterParam as WorkspaceStatusFilter);
      }

      lastUrlSyncRef.current = { id: idParam, filter: filterParam };
    }
    // We intentionally only depend on searchParams — the store setters are
    // stable references from Zustand and changing them shouldn't re-fire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Store -> URL sync. Push current store state to the URL when it diverges.
  // Use replace so history doesn't accumulate on every row click.
  useEffect(() => {
    const currentId = searchParams.get('id');
    const currentFilter = searchParams.get('filter');
    const desiredId = selectedId ? String(selectedId) : null;
    const desiredFilter = statusFilter === 'active' ? null : statusFilter;

    if (currentId === desiredId && currentFilter === desiredFilter) return;

    const next = new URLSearchParams(searchParams);
    if (desiredId) next.set('id', desiredId);
    else next.delete('id');
    if (desiredFilter) next.set('filter', desiredFilter);
    else next.delete('filter');

    lastUrlSyncRef.current = { id: desiredId, filter: desiredFilter };
    setSearchParams(next, { replace: true });
  }, [selectedId, statusFilter, searchParams, setSearchParams]);

  // Resolve the selected row to its list-item DTO (lazy — only when missing
  // or stale). Scoped to the active filter view to avoid an extra fetch in
  // the common case where the user just clicked a visible row.
  useEffect(() => {
    if (!selectedId) {
      setSelectedListItem(null);
      return;
    }
    if (selectedListItem?.id === selectedId) return;
    let cancelled = false;
    quotationService
      .listPaged({ page: 1, pageSize: 100 })
      .then((res) => {
        if (cancelled) return;
        const found = res.items.find((i) => i.id === selectedId);
        if (found) setSelectedListItem(found);
      })
      .catch(() => {
        // Silent — the detail panel can still render from getById alone; the
        // AutoDraft header just won't appear until the next list refresh.
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId, selectedListItem]);

  // `/` shortcut focuses the search input. Bound at the page level so the
  // shortcut works regardless of which pane has focus, but ignored when the
  // user is already typing in an input/textarea.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/') return;
      const tag = (e.target as HTMLElement | null)?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;
      e.preventDefault();
      searchInputRef.current?.focus();
      searchInputRef.current?.select();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleSelect = useCallback(
    (id: number) => {
      setSelectedId(id);
    },
    [setSelectedId],
  );

  // CSC-288: new drafts compose inline in the workspace. Create a blank
  // draft, push it into the shared draftStore (which sets activeId), and
  // clear selectedId so the detail panel renders the unsaved local-only
  // draft instead of trying to fetch by id. The first save assigns a
  // serverId; the detail panel promotes selectedId to that id on save so
  // the URL syncs and the master list picks up the new row.
  const handleCreate = useCallback(() => {
    const blank = createBlankDraft('intake');
    useQuotationDraftStore.getState().loadDraft(blank);
    setSelectedId(null);
  }, [setSelectedId]);

  // 86d3k9589: one-shot deep-link markers from the compose→workspace migration.
  // ?new=1 creates a blank draft (replaces the old /quotations/compose/new route).
  useEffect(() => {
    if (searchParams.get('new') !== '1') return;
    handleCreate();
    const next = new URLSearchParams(searchParams);
    next.delete('new');
    setSearchParams(next, { replace: true });
  }, [searchParams, handleCreate, setSearchParams]);

  // ?review=1 opens the selected draft straight into review mode (replaces the AutoDraft
  // card's old compose pre-warm). Gated on selectedId so it runs AFTER the draft is
  // selected: this parent-level effect fires after QuotationDetailPanel's child-level
  // selectedId-change reset (setReviewMode(false)), so review wins. One-shot — the marker
  // is stripped once consumed.
  useEffect(() => {
    if (searchParams.get('review') !== '1' || !selectedId) return;
    useQuotationDraftStore.getState().setReviewMode(true);
    const next = new URLSearchParams(searchParams);
    next.delete('review');
    setSearchParams(next, { replace: true });
  }, [selectedId, searchParams, setSearchParams]);

  // Bumped after every save in the detail panel — drives QuotationMasterList
  // to refetch so a new draft's row appears (or an updated row's age/status
  // refreshes) without the user having to manually reload.
  const [masterRefreshKey, setMasterRefreshKey] = useState(0);

  // Stats refresh helper — used by mount, masterRefreshKey changes, SSE events,
  // and the master list's onActivity callback (markAsRead, delete). Stable via
  // useCallback so child components can hold the reference safely.
  const refreshStats = useCallback(() => {
    void quotationService
      .getStats()
      .then((s) => setStats(s))
      .catch(() => {
        // Silent — count badges hide gracefully when stats are null.
      });
  }, []);

  // Refetch on mount + after every explicit save. Filter / search changes don't
  // affect global counts so they don't trigger a refetch.
  useEffect(() => {
    refreshStats();
  }, [masterRefreshKey, refreshStats]);

  // CSC-297: realtime stats refresh. The masterRefreshKey effect above only
  // fires after explicit save events; new AutoDrafts arriving via SSE or
  // status flips from auto-archive / extraction-complete don't bump it. Without
  // this listener the unread-AutoDraft pill (and the dropdown count badges)
  // would stay stale until the user changed a filter or saved a draft. Also
  // covers the reconnect catch-up case.
  useEffect(() => {
    const offEvent = onQuotationEvent(refreshStats);
    const offReconnect = onQuotationStreamReconnect(refreshStats);
    return () => {
      offEvent();
      offReconnect();
    };
  }, [refreshStats]);

  const handleAfterSave = useCallback(
    (savedServerId: number) => {
      // First-save promotion: when an unsaved-local draft just got its
      // serverId, sync the URL and selection so subsequent loads round-trip.
      if (savedServerId !== selectedId) setSelectedId(savedServerId);
      setMasterRefreshKey((t) => t + 1);
    },
    [selectedId, setSelectedId],
  );

  // CSC-297: open the realtime SSE stream once at the page level. The master
  // list subscribes to events through the local bus; we don't surface the
  // connectionState in the UI (the prior "Live" pill cluttered the filter bar
  // and shrank the search input). Auto-reconnect is handled inside the hook;
  // the master list listens for reconnect events independently and runs a
  // catch-up refetch.
  useQuotationEventStream();

  return (
    // -m-6 negates AppLayout's p-6 so the workspace fills the viewport edge-
    // to-edge. h-screen fills the full height now that the global top bar is
    // gone.
    <div className="-m-6 flex h-screen flex-col overflow-hidden">
      <QuotationWorkspaceLayout
        master={
          <>
            <QuotationListFilters
              ref={searchInputRef}
              statusFilter={statusFilter}
              onStatusChange={setStatusFilter}
              quickFilter={quickFilter}
              onQuickFilterChange={setQuickFilter}
              stats={stats}
            />
            <QuotationMasterList
              statusFilter={statusFilter}
              quickFilter={quickFilter}
              selectedId={selectedId}
              onSelect={handleSelect}
              refreshKey={masterRefreshKey}
              onActivity={refreshStats}
            />
            {/* CSC-293: sticky FAB for new draft. Lives inside the master pane
                (positioned via the layout's relative wrapper) so the create
                affordance stays reachable regardless of scroll, separated from
                the filter command bar. The list reserves pb-16 so the last
                row never sits underneath the button. */}
            {canCreate && (
              <button
                type="button"
                onClick={handleCreate}
                className="absolute bottom-4 right-4 z-10 inline-flex h-10 items-center gap-1.5 rounded-full bg-primary pl-3 pr-4 text-sm font-medium text-primary-foreground shadow-lg shadow-primary/20 transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label="New draft"
                title="New draft"
              >
                <Plus className="h-4 w-4" />
                New
              </button>
            )}
          </>
        }
        detailHeader={<QuotationPinnedStrip selectedId={selectedId} onSelect={handleSelect} />}
        detail={
          <QuotationDetailPanel
            selectedId={selectedId}
            selectedListItem={selectedListItem}
            onAfterSave={handleAfterSave}
          />
        }
      />
    </div>
  );
}
